// =============================================
// Prompt Loader — pobiera aktywny prompt z tabeli thematic_prompts
// Używany przez SCN serwisy i Content Engine zamiast hardcoded promptów
// =============================================

import { supabase } from "@/integrations/supabase/client";
import { withDateVars } from "@/lib/dateVars";
import type { PromptStep } from "@/types/thematicMaps";

export interface LoadedPrompt {
  template: string;
  model: string;
  /** Provider type: "claude" | "openai" */
  provider: "claude" | "openai";
  /** Specific model ID (e.g. "gpt-4.1", "claude-sonnet-4-5") */
  modelId: string | null;
  /** Temperature override from DB */
  temperature: number | null;
  /** Active row version (DB versioning) — useful for UI/forensics */
  version: number | null;
}

// =============================================
// Standalone mode override — gdy aktywne, loadPrompt('section_generation')
// zwraca 'section_generation_standalone' (klon v3 bez nakazu linków
// wewnętrznych). Inne step-y bez zmian.
//
// REFERENCE COUNTER (NIE bool) — żeby >1 artykuł standalone mogło lecieć
// równolegle bez race na flagi globalnej:
//   - setStandaloneMode(true): count++
//   - setStandaloneMode(false): count = max(0, count-1)
//   - flaga aktywna gdy count > 0
//
// Single artykuł działa identycznie jak przedtem (0→1→0). Bulk z N
// równoległymi artykułami: count rośnie do N, dopiero ostatni decrement
// zwalnia (0). Mapy blokowane przez runLock całą partią (osobny mechanizm).
//
// SCOPE: browser-only. Worker (server-side) ma osobny process.
// =============================================
let standaloneModeCount = 0;

export function setStandaloneMode(flag: boolean): void {
  if (flag) {
    standaloneModeCount++;
  } else {
    standaloneModeCount = Math.max(0, standaloneModeCount - 1);
  }
}

export function isStandaloneMode(): boolean {
  return standaloneModeCount > 0;
}

/** Diagnostic: ile równoległych "standalone" sesji jest aktywnych. */
export function getStandaloneModeCount(): number {
  return standaloneModeCount;
}

/**
 * Pobierz aktywny prompt z bazy danych.
 * Jeśli nie istnieje — zwróci null (serwis użyje fallbacku).
 *
 * Standalone override: gdy standaloneMode === true I step === 'section_generation',
 * loaduje 'section_generation_standalone' zamiast oryginalnego. Inne step-y
 * niezmienione. Gdy flaga false → no-op (zachowanie 1:1 z poprzednim).
 */
export async function loadPrompt(step: PromptStep): Promise<LoadedPrompt | null> {
  const effectiveStep: PromptStep =
    isStandaloneMode() && step === "section_generation"
      ? ("section_generation_standalone" as PromptStep)
      : step;

  try {
    const { data, error } = await (supabase as any)
      .from("thematic_prompts")
      .select("prompt_template, model, provider, model_id, temperature, version")
      .eq("step", effectiveStep)
      .eq("is_active", true)
      .maybeSingle();

    if (error || !data) return null;

    return {
      template: data.prompt_template,
      model: data.model,
      provider: (data.provider || "claude") as "claude" | "openai",
      modelId: data.model_id || null,
      temperature: data.temperature != null ? Number(data.temperature) : null,
      version: data.version != null ? Number(data.version) : null,
    };
  } catch {
    return null;
  }
}

/**
 * Wypełnij zmienne {{zmienna}} w template promptu.
 */
export function fillTemplate(
  template: string,
  vars: Record<string, string>,
): string {
  let result = template;
  // current_year/current_date doklejane automatycznie (fix "zly rok 2025
  // w naglowkach"); vars od wywolujacego wygrywaja przy kolizji nazw.
  for (const [key, value] of Object.entries(withDateVars(vars))) {
    result = result.replaceAll(`{{${key}}}`, value);
  }
  return result;
}
